/** @jsxRuntime classic */
/** @jsx jsx */
import React, { useCallback, useMemo } from 'react';
import { jsx, css } from '@emotion/react';
import { ETypography, ETypographyColor, IColors, Icons } from '../../../types';
import { Typography } from '../../../atoms';
import { BnIcon } from '../../../components/icons_v2';
import { SkeletonWrapper } from '../../../components/skeletonWrapper';
import { Button } from '../../../components/Button';
import { TInvoiceResourceLink, TInvoiceResourceLinkType, TOnResourceClick } from '../types';

const linkButtonCss = css`
	padding: 0 !important;
	justify-content: flex-start;
`;

// Which text line carries the link for each resource type - a new type must declare its placement here
const LINK_PLACEMENT: Record<TInvoiceResourceLinkType, 'name' | 'description'> = {
	membership: 'name',
	reservation: 'description',
	program: 'description',
};

const flex = css`
	display: flex;
`;

const textCss = css`
	display: flex;
	flex-direction: column;
	justify-content: center !important;
	gap: 2px;
	.body1,
	.caption {
		align-items: left !important;
	}
`;

const containerCss = (colors: IColors) => css`
	${flex};
	height: 72px;
	width: 72px;
	justify-content: center !important;
	align-items: center;
	border-radius: 2px;
	margin-inline-end: 8px;
	margin-block-end: 6px;
	background: ${colors.bg_background_primary};
`;

const iconCss = css`
	height: 24px !important;
	width: 24px !important;
`;
type TResourceThumbnailType = 'membership' | 'goods' | 'programSeason' | 'leagueSeason' | 'space' | 'registration';

export const ResourceThumbnail = ({
	resourceType = 'membership',
}: {
	resourceType?: TResourceThumbnailType | (string & {});
}) => {
	const { icon } = useMemo(() => {
		let iconAsset;
		switch (resourceType) {
			case 'membership':
				iconAsset = Icons.membership;
				break;
			case 'programSeason':
			case 'leagueSeason':
			case 'registration':
				iconAsset = Icons.whistle;
				break;
			case 'space':
				iconAsset = (Icons as any).court;
				break;
			case 'goods':
			default:
				iconAsset = Icons.pos_equipment;
				break;
		}
		return { icon: iconAsset };
	}, [resourceType]);

	return (
		<div css={containerCss} data-aid="lineItemColumn-details">
			<BnIcon css={iconCss} icon={icon} />
		</div>
	);
};

interface ILineItemColumnProps {
	name: string;
	description?: string;
	secondDescription?: string;
	img?: string;
	productType?: string;
	isLoading?: boolean;
	resourceLink?: TInvoiceResourceLink;
	onResourceClick?: TOnResourceClick;
	linkableResourceTypes?: TInvoiceResourceLinkType[];
}

const ResourceLinkText = ({
	typography,
	color,
	onClick,
	dataTestId,
	children,
}: {
	typography: ETypography;
	color: ETypographyColor;
	onClick: () => void;
	dataTestId: string;
	children: React.ReactNode;
}) => (
	<Button data-testid={dataTestId} type="button" sizer="XS" theme="basic" css={linkButtonCss} onClick={onClick}>
		<Typography type={typography} color={color}>
			{children}
		</Typography>
	</Button>
);

export const LineItemColumn = ({
	name,
	description,
	secondDescription,
	img,
	productType,
	isLoading,
	resourceLink,
	onResourceClick,
	linkableResourceTypes,
}: ILineItemColumnProps) => {
	const canLink = Boolean(
		resourceLink && onResourceClick && (!linkableResourceTypes || linkableResourceTypes.includes(resourceLink.type))
	);
	const linkPlacement = canLink && resourceLink ? LINK_PLACEMENT[resourceLink.type] : undefined;
	const isNameLink = linkPlacement === 'name';
	const isDescriptionLink = linkPlacement === 'description' && Boolean(description);
	const handleClick = useCallback(() => {
		if (resourceLink && onResourceClick) {
			onResourceClick(resourceLink);
		}
	}, [onResourceClick, resourceLink]);

	return (
		<div css={flex}>
			{img ? (
				<img src={img} />
			) : (
				//  Resource thumbnail
				<ResourceThumbnail resourceType={productType} />
			)}
			<div css={textCss}>
				<SkeletonWrapper isLoading={isLoading} minHeight={6} minWidth={80}>
					{isNameLink ? (
						<ResourceLinkText
							typography={ETypography.body1Link}
							color={ETypographyColor.primary}
							onClick={handleClick}
							dataTestId="line-item-resource-link-name"
						>
							{name}
						</ResourceLinkText>
					) : (
						<Typography type={ETypography.body1} color={ETypographyColor.primary}>
							{name}
						</Typography>
					)}
				</SkeletonWrapper>
				<SkeletonWrapper isLoading={isLoading} minHeight={6} minWidth={80}>
					{isDescriptionLink ? (
						<ResourceLinkText
							typography={ETypography.captionLink}
							color={ETypographyColor.secondary}
							onClick={handleClick}
							dataTestId="line-item-resource-link-description"
						>
							{description}
						</ResourceLinkText>
					) : (
						<Typography type={ETypography.caption} color={ETypographyColor.secondary}>
							{description}
						</Typography>
					)}
				</SkeletonWrapper>
				<SkeletonWrapper isLoading={isLoading} minHeight={6} minWidth={80}>
					<Typography type={ETypography.caption} color={ETypographyColor.secondary}>
						{secondDescription}
					</Typography>
				</SkeletonWrapper>
			</div>
		</div>
	);
};
